import { useEffect, useState } from "react";
import { Area, AreaChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { fmtBps, fmtBytes, fmtDur } from "@/lib/api";
import { usePoll, PageHeader, Panel, Stat } from "@/components/common";
import { Link } from "react-router-dom";

function Chart({ data, keys, fmt }) {
  return (
    <ResponsiveContainer width="100%" height={160}>
      <AreaChart data={data}>
        <XAxis dataKey="t" hide />
        <YAxis tick={{ fill: "#64748B", fontSize: 10 }} tickFormatter={fmt} width={60} />
        <Tooltip contentStyle={{ background: "#16202E", border: "1px solid #334155", fontSize: 11 }} formatter={(v) => fmt(v)} />
        {keys.map(([k, c]) => <Area key={k} type="monotone" dataKey={k} stroke={c} fill={c} fillOpacity={0.12} strokeWidth={1.5} isAnimationActive={false} />)}
      </AreaChart>
    </ResponsiveContainer>
  );
}

function CostTable() {
  const { data } = usePoll("/admin/channels", 10000);
  const rows = (data || []).filter((c) => c.cost).sort((a, b) => b.cost.cpu - a.cost.cpu);
  const tot = rows.reduce((a, c) => ({ cpu: a.cpu + c.cost.cpu, i: a.i + c.cost.in_bps, o: a.o + c.cost.out_bps }), { cpu: 0, i: 0, o: 0 });
  return (
    <Panel title="Channel cost" className="mt-4" testid="channel-cost-table">
      <table className="w-full text-xs font-mono">
        <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">{["Channel", "CPU", "Upstream in", "Served out", "Viewers", "Pushes"].map((h) => <th key={h} className="pb-2 font-semibold">{h}</th>)}</tr></thead>
        <tbody>
          {rows.map((c) => (
            <tr key={c.id} data-testid={`cost-row-${c.slug}`} className="border-t border-slate-800/60">
              <td className="py-1.5"><Link to={`/channels/${c.id}`} className="text-cyan-300 hover:underline">{c.slug}</Link></td>
              <td className="text-slate-200">{c.cost.cpu}%</td><td className="text-emerald-400">{fmtBps(c.cost.in_bps)}</td>
              <td className="text-amber-400">{fmtBps(c.cost.out_bps)}</td><td className="text-slate-300">{c.cost.viewers}</td><td className="text-slate-300">{c.cost.pushes}</td>
            </tr>
          ))}
          {rows.length > 0 && <tr data-testid="cost-row-total" className="border-t border-slate-700 text-slate-100"><td className="py-1.5">total</td><td>{tot.cpu.toFixed(1)}%</td><td>{fmtBps(tot.i)}</td><td>{fmtBps(tot.o)}</td><td /><td /></tr>}
        </tbody>
      </table>
      {rows.length === 0 && <p className="text-sm text-slate-500">No running channels (cost appears ~10 s after start).</p>}
      <p className="text-[11px] text-slate-500 mt-2">CPU is the sum of a channel's ffmpeg processes (packager, slate, RTMP pushes) as % of one core. Out = bytes served to servers + pushes.</p>
    </Panel>
  );
}

export default function Node() {
  const { data: n } = usePoll("/admin/node", 3000);
  const [hist, setHist] = useState([]);
  useEffect(() => { if (n) setHist((h) => [...h.slice(-59), { t: n.time, cpu: n.cpu_percent, ram: n.ram_percent, rx: n.net_in_bps, tx: n.net_out_bps }]); }, [n]);
  if (!n) return <div className="text-slate-500 font-mono text-sm">loading…</div>;
  return (
    <div>
      <PageHeader title="Node" sub={<span className="font-mono">{n.build_version}</span>} />
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-2 mb-4">
        <Stat label="CPU" value={`${n.cpu_percent.toFixed(1)}% · ${n.cpu_count} cores`} testid="node-cpu" />
        <Stat label="RAM" value={`${n.ram_percent.toFixed(1)}%`} testid="node-ram" />
        <Stat label="Net in" value={fmtBps(n.net_in_bps)} testid="node-net-in" />
        <Stat label="Net out" value={fmtBps(n.net_out_bps)} testid="node-net-out" />
        <Stat label="Disk" value={`${n.disk_percent.toFixed(0)}% · ${fmtBytes(n.disk_used)}`} testid="node-disk" />
        <Stat label="Channels running" value={n.channels_running} testid="node-channels" />
        <Stat label="RTMP pushes" value={n.push_targets} />
        <Stat label="Uptime" value={fmtDur(n.uptime)} />
      </div>
      <div className="grid xl:grid-cols-2 gap-4">
        <Panel title="CPU / RAM %"><Chart data={hist} keys={[["cpu", "#06B6D4"], ["ram", "#3B82F6"]]} fmt={(v) => `${Number(v).toFixed(0)}%`} /></Panel>
        <Panel title="Network in / out"><Chart data={hist} keys={[["rx", "#10B981"], ["tx", "#F59E0B"]]} fmt={fmtBps} /></Panel>
      </div>
      <CostTable />
      <p className="text-xs text-slate-500 mt-4">Sandbox capacity is about 3–5 channels running at once; each RTMP push target and MPEG-TS viewer adds load. Disk: {fmtBytes(n.disk_used)} / {fmtBytes(n.disk_total)}.</p>
    </div>
  );
}
